import { TextInput } from "@sede-enterprise/shell-ds-react-framework";
import Decimal from "decimal.js";
import type { ChangeEvent, Dispatch, ReactElement } from "react";
import {
  AMERICAS,
  FETCH_ERROR,
  NATURAL_GAS,
  PIPELINE_GAS,
  RNG,
  TABLE_ROW_CARBON_CREDIT,
  TABLE_ROW_TOTAL_VOLUME,
  TECHSOURCE_TWH,
} from "../../api/constants";
import {
  fetchCarbonIntensityForPipelineGasAsCommodity,
  fetchCarbonIntensityForRngAsCommodity,
  fetchCarbonIntensityGrid,
  fetchCarbonIntensityNonGrid,
  fetchGasFiredPowerData,
  fetchProductCarbonIntensities,
  fetchSLMTCarbonIntensities,
} from "../../api/services";
import { one, two } from "../../constants";
import type {
  Action,
  CiDataInterface,
  IArrayOfObject,
  ICIforCommodityPipelineGas,
  ICIforCommodityRng,
  ICIforGrid,
  ICalculatorFormState,
  IProductCarbonIntensity,
  IStepTwoTableColumnObject,
  IStepTwoTableData,
  ITableData,
  Idispatch,
  InputTableFormState,
} from "../../types/types";
import {
  convertCopiedInputToCommaSeparatedWithTwoDecimal,
  convertInputToCommaSeparatedWithTwoDecimal,
} from "../../utils";
import { notify } from "../Toast/notify";

let isDataCopied = false;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let ciData: object[];
const carbonIntensities: object[] = [];

// Helper function to normalize EVP name for comparison
const normalizeEvpName = (evpName: string): string => {
  return evpName.toLowerCase().replace(/[^a-z0-9]/g, "");
};

// Helper function to safely get commodity as string from string | string[]
const getCommodityAsString = (commodity: string | string[]): string => {
  return Array.isArray(commodity) ? commodity[0] || "" : commodity;
};

// Helper to handle T&S Products row setup
const handleTSProductsRows = (state: ICalculatorFormState, firstColData: string[]) => {
  const normalizedEvp = normalizeEvpName(state.evp);
  if (
    normalizedEvp === "tsproducts" &&
    Array.isArray(state.commodity) &&
    state.commodity.length > 0
  ) {
    firstColData.unshift(...state.commodity);
    // Add Carbon Credits row for TSP Carbon Credit YES (1)
    if (state.carbonCredit === 1) {
      firstColData.push(TABLE_ROW_CARBON_CREDIT);
    }
    return true;
  }
  return false;
};

// Helper to handle SLMT row setup
const handleSLMTRows = (state: ICalculatorFormState, firstColData: string[]) => {
  const normalizedEvp = normalizeEvpName(state.evp);
  if (normalizedEvp === "slmt" && state.commodity && state.commodity !== "") {
    const commodityValue = Array.isArray(state.commodity) ? state.commodity[0] : state.commodity;
    firstColData.unshift(commodityValue);
    return true;
  }
  return false;
};

// Helper to handle commodity-based row setup
const handleCommodityRows = (state: ICalculatorFormState, firstColData: string[]) => {
  const commodityLower = getCommodityAsString(state.commodity).toLowerCase();

  if (commodityLower === "power" && state.technologySource.length > 0) {
    firstColData.unshift(...state.technologySource);
  } else if (commodityLower.includes("gas")) {
    firstColData.unshift(PIPELINE_GAS);
  } else if (commodityLower.includes("rng")) {
    firstColData.unshift(RNG);
  }
};

export const bootstrapCol = (
  col: Array<string | number>,
  state: ICalculatorFormState,
  firstColData: string[],
  myDispatch: Idispatch
) => {
  // Handle different EVP types and commodities
  if (!handleTSProductsRows(state, firstColData) && !handleSLMTRows(state, firstColData)) {
    handleCommodityRows(state, firstColData);
  }

  if (state.startYear !== 0 && state.endYear !== 0) {
    for (let i = state.startYear; i <= state.endYear; i++) {
      col.push(i);
    }
  }
  myDispatch({
    type: "setCols",
    payload: col,
  });
  return col;
};

export const decorateTableData = (
  firstColData: string[],
  dispatch: Idispatch,
  col: Array<string | number>
) => {
  const data: Array<Record<string, string | number>> = [];
  const firstCol = col[0];
  // setting first key-value pair for each row object
  for (const element of firstColData) {
    data.push({ [firstCol]: element });
  }
  for (const row of data) {
    for (let j = 1; j < col.length; j++) {
      row[col[j]] = "";
    }
  }
  dispatch({
    type: "setStepTwoTableData",
    payload: data,
  });
};

export const removeExtraRowFromCopiedContent = (rows: string[][]) => {
  if (rows[rows.length - 1].length === 1 && rows[rows.length - 1][0] === "") {
    rows.pop();
  }
};

const hanldePastingData = (
  val: number,
  index: number,
  rows: string[][],
  state: ICalculatorFormState,
  dispatch: Idispatch
) => {
  const currTableData: IStepTwoTableData[] = [...state.stepTwoTableData] as IStepTwoTableData[];
  const currColYearBeingChanged: number = val;
  const currRowBeingChanged: number = index;

  // Calculate the index of Total Volume row to skip it during paste
  const normalizedEvp = normalizeEvpName(state.evp);
  const hasCarbonCredits = normalizedEvp === "tsproducts" && state.carbonCredit === 1;
  const totalVolumeIndex = hasCarbonCredits ? currTableData.length - 2 : currTableData.length - 1;

  // start pasting dynamically from the row-col index user initiates the pasting
  for (let i = 0; i < currTableData.length - currRowBeingChanged && i < rows.length; i++) {
    const targetRow = currRowBeingChanged + i;
    // Skip Total Volume row during paste
    if (targetRow === totalVolumeIndex) continue;

    for (let j = 0; j < state.endYear - currColYearBeingChanged + 1 && j < rows[i].length; j++) {
      const year = currColYearBeingChanged + j;
      (currTableData[targetRow] as unknown as Record<number, string>)[year] =
        convertCopiedInputToCommaSeparatedWithTwoDecimal(rows[i][j]);
    }
  }
  // summing up for Total Volume
  // totalVolumeIndex already calculated above

  let sum = 0;
  for (let year = state.startYear; year < state.endYear + 1; year++) {
    // Sum only commodity rows (before Total Volume index)
    for (let i = 0; i < totalVolumeIndex; i++) {
      sum =
        sum +
        Number(
          (currTableData[i] as unknown as Record<number, string>)[year]
            .toString()
            .replaceAll(",", "")
        );
    }
    // Safety check: Only update Total Volume row if it exists
    if (totalVolumeIndex >= 0 && currTableData[totalVolumeIndex]) {
      (currTableData[totalVolumeIndex] as unknown as Record<number, string>)[year] =
        convertCopiedInputToCommaSeparatedWithTwoDecimal(sum.toString());
    }
    sum = 0;
  }
  dispatch({
    type: "setStepTwoTableData",
    payload: currTableData,
  });
};

const pasteCopiedDataToTable = async (
  val: number,
  index: number,
  state: ICalculatorFormState,
  dispatch: Idispatch,
  myDispatch: Idispatch
) => {
  // pasteDataFromClipboard(currTableData, val,row);
  // Get the pasted text from the clipboard data
  let pastedText = "";

  pastedText = await navigator.clipboard.readText();

  // Split the pasted text into rows and columns
  const rows = pastedText.split("\r\n").map((row) => row.split("\t"));
  // sometimes pasted text contains an empty row at the end. remove that.
  removeExtraRowFromCopiedContent(rows);
  let hasPastedTextValidNumbers = true;

  // check if the values of pastedText include only valid numbers (positive, negative, or decimals)
  rows.some((row) => {
    return row.some((cellData) => {
      if (typeof cellData === "string" && cellData.includes(",")) {
        cellData = cellData.replaceAll(",", "");
      }
      hasPastedTextValidNumbers = /^-?(\d+(\.\d+)?|\.\d+|0)$/.test(cellData);
      return !hasPastedTextValidNumbers;
    });
  });

  if (hasPastedTextValidNumbers) {
    hanldePastingData(val, index, rows, state, dispatch);
  } else {
    // throw error that user is trying to paste unacceptable values
    myDispatch({
      type: "setIsPasteDataFailure",
      payload: true,
    });
  }
  // make the variable false especially for scenarios when user tries to copy and then start typing
  isDataCopied = false;
};

const calculateTableSum = (
  currTableData: ICalculatorFormState[],
  val: number,
  state: ICalculatorFormState
): { sum: Decimal; tableData: ITableData } => {
  const tableData: ITableData = {};
  let sum = new Decimal(0);

  // If Carbon Credits exists, Total Volume row is second to last; otherwise it's last
  const normalizedEvp = normalizeEvpName(state.evp);
  const hasCarbonCredits = normalizedEvp === "tsproducts" && state.carbonCredit === 1;
  const totalVolumeIndex = hasCarbonCredits ? currTableData.length - 2 : currTableData.length - 1;

  currTableData.forEach((row, index) => {
    // Sum only commodity rows (before Total Volume)
    if (index < totalVolumeIndex) {
      const cellValue = (row as unknown as Record<number, string>)[val]
        .toString()
        .replace(/,/g, "");
      sum = sum.plus(new Decimal(cellValue || 0)); // Treat empty string as 0

      for (let year = state.startYear; year <= state.endYear; year++) {
        const yearCellValue = (row as unknown as Record<number, string>)[year];
        tableData[`${index}${year}`] = yearCellValue || 0; // Handle empty string
      }
    }
  });

  return { sum, tableData };
};

const handleManualValeChangeInTable = (
  e: MouseEvent | ChangeEvent<HTMLInputElement>,
  row: IStepTwoTableData,
  val: number,
  tableHeadingOne: string,
  state: ICalculatorFormState,
  dispatch: Idispatch
) => {
  const currTableData: ICalculatorFormState[] = [
    ...state.stepTwoTableData,
  ] as unknown as ICalculatorFormState[];
  const inputField = e.target as HTMLInputElement;
  isDataCopied = false; // Reset copy state

  // Allow negative values and handle leading zeros
  let value = inputField.value;
  if (value === "-") {
    handleNegativeValue(currTableData, tableHeadingOne, row, val, "-", dispatch);
    return;
  } else if (value.startsWith("0") && value.length > 1 && !value.startsWith("0.")) {
    value = value.slice(1); // Remove leading zero unless it's a decimal
  }

  const valueToShowInUI = convertInputToCommaSeparatedWithTwoDecimal(
    value.toString().replaceAll(",", "")
  );
  for (const [i, rowData] of currTableData.entries()) {
    if (
      rowData[tableHeadingOne as keyof typeof rowData] === row[tableHeadingOne as keyof typeof row]
    ) {
      (currTableData[i] as unknown as Record<number, string>)[val] = valueToShowInUI;
      break;
    }
  }

  const { sum, tableData } = calculateTableSum(currTableData, val, state);

  // If Carbon Credits exists, Total Volume is second to last; otherwise it's last
  const normalizedEvp = normalizeEvpName(state.evp);
  const hasCarbonCredits = normalizedEvp === "tsproducts" && state.carbonCredit === 1;
  const totalVolumeIndex = hasCarbonCredits ? currTableData.length - 2 : currTableData.length - 1;

  // Safety check: Only update Total Volume row if it exists
  if (totalVolumeIndex >= 0 && currTableData[totalVolumeIndex]) {
    (currTableData[totalVolumeIndex] as unknown as Record<number, string>)[val] =
      convertInputToCommaSeparatedWithTwoDecimal(sum.toString());
  }
  dispatch({
    type: "setUserTableInputData",
    payload: tableData,
  });
  dispatch({
    type: "setStepTwoTableData",
    payload: currTableData,
  });
};

const handleNegativeValue = (
  currTableData: ICalculatorFormState[],
  tableHeadingOne: string,
  row: IStepTwoTableData,
  val: number,
  value: string,
  dispatch: Idispatch
) => {
  // If input is just '-' or '0-', update UI but skip further processing - adding extra comment
  for (const [i, rowData] of currTableData.entries()) {
    if (
      rowData[tableHeadingOne as keyof typeof rowData] === row[tableHeadingOne as keyof typeof row]
    ) {
      (currTableData[i] as unknown as Record<number, string>)[val] = value === "0-" ? "-" : value;
      break;
    }
  }
  dispatch({
    type: "setStepTwoTableData",
    payload: currTableData,
  });
};

const handleStepTwoTableValueChanges = async (
  e: MouseEvent | ChangeEvent<HTMLInputElement>,
  data: {
    val: number;
    index: number;
    row: IStepTwoTableData;
    tableHeadingOne: string;
  },
  state: ICalculatorFormState,
  dispatch: Idispatch,
  myDispatch: Idispatch
) => {
  const { val, index, row, tableHeadingOne } = data;
  if (isDataCopied) {
    await pasteCopiedDataToTable(val, index, state, dispatch, myDispatch);
  } else {
    handleManualValeChangeInTable(e, row, val, tableHeadingOne, state, dispatch);
  }
};

const handleDataPasteFromExternalFile = () => {
  isDataCopied = true;
};

const updateCIdataForGrid = (response: ICIforGrid[]) => {
  const ciDataObj: CiDataInterface = { [TECHSOURCE_TWH]: "" };
  response.forEach((resp) => {
    const { TECHNOLOGY_SOURCE_NAME, YEAR, CARBON_INTENSITY } = resp;
    if (ciDataObj[TECHSOURCE_TWH] === "") {
      ciDataObj[TECHSOURCE_TWH] = TECHNOLOGY_SOURCE_NAME;
    }
    ciDataObj[YEAR] = CARBON_INTENSITY;
  });
  carbonIntensities.push(ciDataObj);
};

const updateCIdataForNonGrid = (response: IArrayOfObject, state: ICalculatorFormState) => {
  response.forEach((resp) => {
    const { TECHNOLOGY_SOURCE_NAME, CARBON_INTENSITY } = resp;
    const ciDataObj = {
      [TECHSOURCE_TWH]: TECHNOLOGY_SOURCE_NAME,
    };
    for (let year = state.startYear; year < state.endYear + 1; year++) {
      ciDataObj[year as unknown as keyof typeof ciDataObj] = CARBON_INTENSITY;
    }
    carbonIntensities.push(ciDataObj);
  });
};

const updateCIdataForGas = (response: ICIforCommodityPipelineGas[]) => {
  const ciDataObj: CiDataInterface = { [TECHSOURCE_TWH]: PIPELINE_GAS };
  response.forEach((resp) => {
    const { YEAR, CARBON_INTENSITY } = resp;
    ciDataObj[YEAR] = CARBON_INTENSITY;
  });
  carbonIntensities.push(ciDataObj);
};

const updateCIdataForRng = (response: ICIforCommodityRng[]) => {
  const ciDataObj: CiDataInterface = { [TECHSOURCE_TWH]: "RNG" };
  response.forEach((resp) => {
    const { YEAR, CARBON_INTENSITY } = resp;
    ciDataObj[YEAR] = CARBON_INTENSITY;
  });
  carbonIntensities.push(ciDataObj);
};
type ICIforCommodity = ICIforCommodityPipelineGas | ICIforCommodityRng;
const updateCIdata = (
  ciFor: string,
  state: ICalculatorFormState,
  response?: ICIforCommodity[] | ICIforGrid[] | IArrayOfObject
) => {
  // Add grid carbon intensities to the carbonIntensities object
  if (ciFor === "grid") {
    updateCIdataForGrid(response as ICIforGrid[]);
  }
  // Add non-grid carbon intensities to the carbonIntensities object
  if (ciFor === "nongrid") {
    updateCIdataForNonGrid(response as IArrayOfObject, state);
  }
  if (ciFor === "isGas") {
    updateCIdataForGas(response as ICIforCommodityPipelineGas[]);
  }
  if (ciFor === "isRng") {
    updateCIdataForRng(response as ICIforCommodityRng[]);
  }
  ciData = [...carbonIntensities];
};

const showLoading = (isLoading: boolean, myDispatch: Dispatch<Action>) => {
  myDispatch({
    type: "setIsLoading",
    payload: isLoading,
  });
};

const getCIforCommodityRng = async (
  myDispatch: Dispatch<Action>,
  state: ICalculatorFormState,
  dispatch: Idispatch
) => {
  let rngResp;
  // Api to get CI for renewable gas
  const options = {
    region: state.region,
    startYear: state.startYear,
    endYear: state.endYear,
  };
  showLoading(true, myDispatch);
  try {
    rngResp = await fetchCarbonIntensityForRngAsCommodity(options);
    const responseData: ICIforCommodityRng[] = rngResp?.data;
    // Carbon intensity value with UOM gCO2e/MJ only should be used for calculating Average carbon intensity.
    const carbonIntForRngAsCommodity = responseData?.filter(
      (rng) => rng?.CARBON_INTENSITY_UNIT === "gCO2e/MJ"
    );
    updateCIdata("isRng", state, carbonIntForRngAsCommodity);
    dispatch({
      type: "setCarbonIntensityForRng",
      payload: carbonIntForRngAsCommodity,
    });
    dispatch({
      type: "setIsShowKpiData",
      payload: true,
    });
  } catch {
    dispatch({
      type: "setIsGetOutputKpiServiceFailure",
      payload: true,
    });
    dispatch({
      type: "setIsShowKpiData",
      payload: false,
    });
    notify("error", FETCH_ERROR);
    showLoading(false, myDispatch);
  }
  showLoading(false, myDispatch);
};

const getCIforCommodityPipelineGas = async (
  myDispatch: Dispatch<Action>,
  state: ICalculatorFormState,
  dispatch: Idispatch
) => {
  let nonGridResp;
  // Api to get CI for natural gas with Americas
  const options = {
    region: state.region,
    country: state.country,
    startYear: state.startYear,
    endYear: state.endYear,
  };
  // Pipeline Gas Carbon Intensity API: Fetch CI data for pipeline gas commodities
  // Used by non-SLMT EVPs for standard carbon intensity calculations
  showLoading(true, myDispatch);
  try {
    nonGridResp = await fetchCarbonIntensityForPipelineGasAsCommodity(options);
    const responsedata: ICIforCommodityPipelineGas[] = nonGridResp?.data;
    // Carbon intensity value with UOM gCO2e/MJ only should be used for calculating Average carbon intensity.
    const carbonIntForPipelineGasAsCommodity = responsedata?.filter(
      (PipelineGas) => PipelineGas?.CARBON_INTENSITY_UNIT === "gCO2e/MJ"
    );
    updateCIdata("isGas", state, carbonIntForPipelineGasAsCommodity);
    dispatch({
      type: "setCarbonIntensityForGas",
      payload: carbonIntForPipelineGasAsCommodity,
    });
    dispatch({
      type: "setIsShowKpiData",
      payload: true,
    });
  } catch {
    dispatch({
      type: "setIsGetOutputKpiServiceFailure",
      payload: true,
    });
    dispatch({
      type: "setIsShowKpiData",
      payload: false,
    });
    notify("error", FETCH_ERROR);
    showLoading(false, myDispatch);
  }
  showLoading(false, myDispatch);
};

const getCIforCommodityTSProducts = async (
  myDispatch: Dispatch<Action>,
  state: ICalculatorFormState,
  dispatch: Idispatch
) => {
  let productResp;
  // Get the list of products from commodity (can be string or array)
  const products = Array.isArray(state.commodity) ? state.commodity : [state.commodity];

  // Always pass both `region` and `country` to the service. The service will
  // include the appropriate query params depending on the product types.
  const options = { products, region: state.region, country: state.country };

  showLoading(true, myDispatch);
  try {
    productResp = await fetchProductCarbonIntensities(options);
    const responsedata: IProductCarbonIntensity[] = productResp?.data;

    dispatch({
      type: "setProductCarbonIntensities",
      payload: responsedata,
    });
    dispatch({
      type: "setIsShowKpiData",
      payload: true,
    });
  } catch {
    dispatch({
      type: "setIsGetOutputKpiServiceFailure",
      payload: true,
    });
    dispatch({
      type: "setIsShowKpiData",
      payload: false,
    });
    notify("error", FETCH_ERROR);
    showLoading(false, myDispatch);
  }
  showLoading(false, myDispatch);
};

const getCIforCommoditySLMT = async (
  myDispatch: Dispatch<Action>,
  state: ICalculatorFormState,
  dispatch: Idispatch
) => {
  // SLMT uses backend API for carbon intensity
  let slmtResp;
  showLoading(true, myDispatch);
  try {
    // countryAssetOfSupplyId, commodityId, startYear, endYear must be present in state
    const countryAssetOfSupplyId = state.countryId;
    const commodityId = Array.isArray(state.commodityId) ? state.commodityId[0] : state.commodityId;
    const startYear = state.startYear;
    const endYear = state.endYear;

    if (!countryAssetOfSupplyId || commodityId == null || !startYear || !endYear) {
      throw new Error("Missing required parameters for SLMT CI API");
    }

    const slmtCarbonIntensityOptions = {
      commodityId,
      startYear,
      endYear,
    };
    // For SLMT, if type of supply is 1 means Shell Energy then only we need to send countryAssetOfSupplyId
    // to get the carbon intensity data.
    // for other type of supply like Third Party we can send the request without countryAssetOfSupplyId
    // so that API will treat this value as null and return default carbon intensity values.
    if (state.typeOfSupplyId === 1) {
      slmtCarbonIntensityOptions[
        "countryAssetOfSupplyId" as keyof typeof slmtCarbonIntensityOptions
      ] = countryAssetOfSupplyId;
    }
    slmtResp = await fetchSLMTCarbonIntensities(slmtCarbonIntensityOptions);
    const responseData = slmtResp?.data;
    // Optionally filter/process responseData as needed
    dispatch({
      type: "setCarbonIntensityForSLMT",
      payload: responseData,
    });
    dispatch({
      type: "setIsShowKpiData",
      payload: true,
    });
  } catch {
    dispatch({
      type: "setIsGetOutputKpiServiceFailure",
      payload: true,
    });
    dispatch({
      type: "setIsShowKpiData",
      payload: false,
    });
    notify("error", FETCH_ERROR);
    showLoading(false, myDispatch);
  }
  showLoading(false, myDispatch);
};

const getCIforCommodityPower = async (
  myDispatch: Dispatch<Action>,
  state: ICalculatorFormState,
  doesTechSrcHasGrid: boolean,
  doesTechSrcHasGasFiredPower: boolean,
  dispatch: Idispatch
) => {
  let nonGridResp;
  if (!doesTechSrcHasGrid && !doesTechSrcHasGasFiredPower) {
    // when technology source list has no grid and no gas fired power
    showLoading(true, myDispatch);
    try {
      nonGridResp = await fetchCarbonIntensityNonGrid(state.technologySource);
      const carbonIntForNonGrid = nonGridResp?.data;
      updateCIdata("nongrid", state, carbonIntForNonGrid);
      dispatch({
        type: "setCarbonIntensityForNonGrid",
        payload: carbonIntForNonGrid,
      });
      dispatch({
        type: "setIsShowKpiData",
        payload: true,
      });
    } catch {
      dispatch({
        type: "setIsGetOutputKpiServiceFailure",
        payload: true,
      });
      dispatch({
        type: "setIsShowKpiData",
        payload: false,
      });
      notify("error", FETCH_ERROR);
      showLoading(false, myDispatch);
    }
    showLoading(false, myDispatch);
  } else {
    // when tech src has combined grid/non-grid/gas fired power
    const techSrc = [...state.technologySource];
    // get the index of GRID out ot tech source list
    const grid = doesTechSrcHasGrid ? techSrc.splice(techSrc.indexOf("Grid"), 1) : [];
    let gridResp;
    let gasFiredPowerResp;
    const optionsForGrid = {
      techSource: grid,
      startYear: state.startYear,
      endYear: state.endYear,
      gridLocation: state.gridLocation,
    };
    showLoading(true, myDispatch);
    try {
      // fetch carbon intensity for Grid
      if (doesTechSrcHasGrid) {
        gridResp = await fetchCarbonIntensityGrid(optionsForGrid);
        const carbonIntForGrid = gridResp?.data;
        updateCIdata("grid", state, carbonIntForGrid);
        dispatch({
          type: "setCarbonIntensityForGrid",
          payload: carbonIntForGrid,
        });
      }

      // fetch CI for gas fired power
      if (doesTechSrcHasGasFiredPower) {
        // remove LNG or Natural Gas from tech source
        updateTechSrcToRemoveGFP(techSrc);
        const options = {
          startYear: state.startYear,
          endYear: state.endYear,
        };
        gasFiredPowerResp = await fetchGasFiredPowerData(options);
        const gasFiredPowerData = gasFiredPowerResp?.data;
        dispatch({
          type: "setGasFiredPowerTableData",
          payload: gasFiredPowerData,
        });
      }
      // fetch carbon intensity for non-Grid
      if (techSrc.length > 0) {
        // after splicing grid and gas fired power, if techSrc has some value then thats for non-grid
        nonGridResp = await fetchCarbonIntensityNonGrid(techSrc);
        const carbonIntForNonGrid = nonGridResp?.data;
        updateCIdata("nongrid", state, carbonIntForNonGrid);
        dispatch({
          type: "setCarbonIntensityForNonGrid",
          payload: carbonIntForNonGrid,
        });
      }

      dispatch({
        type: "setIsShowKpiData",
        payload: true,
      });
    } catch {
      dispatch({
        type: "setIsGetOutputKpiServiceFailure",
        payload: true,
      });
      dispatch({
        type: "setIsShowKpiData",
        payload: false,
      });
      notify("error", FETCH_ERROR);
      showLoading(false, myDispatch);
    }
    showLoading(false, myDispatch);
  }
};

const updateTechSrcToRemoveGFP = (techSrc: string[]) => {
  if (techSrc.includes("LNG") || techSrc.includes(PIPELINE_GAS)) {
    // Remove 'LNG' and 'Natural Gas' from techSrc
    const indexLNG = techSrc.indexOf("LNG");
    const indexPipelineGas = techSrc.indexOf("Natural Gas");
    if (indexLNG !== -1) {
      techSrc.splice(indexLNG, 1);
    }
    if (indexPipelineGas !== -1) {
      techSrc.splice(indexPipelineGas, 1);
    }
  }
};

export const getOutputKpi = async (
  state: ICalculatorFormState,
  dispatch: Idispatch,
  myDispatch: Dispatch<Action>
) => {
  const isAmericasPipelineGas = state.commodity === PIPELINE_GAS && state.region === AMERICAS;
  const isRestOfTheWorldPipelineGas = state.commodity === PIPELINE_GAS && state.region !== AMERICAS;
  const isCommodityPipelineGas = isAmericasPipelineGas || isRestOfTheWorldPipelineGas;
  const isCommodityRng = state.commodity === "RNG";
  const isPower = getCommodityAsString(state.commodity).toLowerCase() === "power";
  const doesTechSrcHasGrid = state.technologySource.includes("Grid");
  const doesTechSrcHasGasFiredPower =
    state.technologySource.includes(NATURAL_GAS) || state.technologySource.includes("LNG");

  // Check if this is T&S Products or SLMT EVP
  const normalizedEvp = normalizeEvpName(state.evp);
  const isTSProducts = normalizedEvp === "tsproducts";
  const isSLMT = normalizedEvp === "slmt";

  // Fetch carbon intensity data based on commodity and technology source selections
  if (isCommodityPipelineGas) {
    await getCIforCommodityPipelineGas(myDispatch, state, dispatch);
  } else if (isPower && state.technologySource.length > 0) {
    await getCIforCommodityPower(
      myDispatch,
      state,
      doesTechSrcHasGrid,
      doesTechSrcHasGasFiredPower,
      dispatch
    );
  } else if (isCommodityRng) {
    await getCIforCommodityRng(myDispatch, state, dispatch);
  } else if (isTSProducts) {
    // Fetch T&S Products carbon intensities from API
    await getCIforCommodityTSProducts(myDispatch, state, dispatch);
  } else if (isSLMT) {
    // Fetch SLMT carbon intensities from API
    await getCIforCommoditySLMT(myDispatch, state, dispatch);
  }

  dispatch({
    type: "setIsGetOutputKpi",
    payload: !state.isGetOutputKpi,
  });
  dispatch({
    type: "setActiveKey",
    payload: [0, 1, two],
  });
};

export const resetTable = (state: ICalculatorFormState, dispatch: Idispatch) => {
  const currTableData: IStepTwoTableData[] = [...state.stepTwoTableData] as IStepTwoTableData[];
  for (const rowData of currTableData) {
    for (let year = state.startYear; year < state.endYear + 1; year++) {
      rowData[year] = 0;
    }
  }
  dispatch({
    type: "setStepTwoTableData",
    payload: currTableData,
  });
};

const getFirstColumnData = (commodity: string | string[], value: string) => {
  const commodityMap: Record<string, Record<string, string>> = {
    power: {
      PIPELINE_GAS: "Gas Fired Generation (NG)",
      LNG: "Gas Fired Generation (LNG)",
    },
  };
  // Handle both string and array types for commodity
  const commodityStr = Array.isArray(commodity) ? commodity[0] || "" : commodity;
  const commodityLower = commodityStr.toLowerCase();
  const mappedValue = commodityMap[commodityLower]?.[value];
  return <>{mappedValue || value}</>;
};

export const getColumnData = (
  data: {
    row: IStepTwoTableData;
    tableHeadingOne: string;
    value: string | number;
    val: string | number;
    index: number;
  },
  state: ICalculatorFormState,
  dispatch: Idispatch,
  myDispatch: Idispatch
) => {
  const { row, tableHeadingOne, value, val, index } = data;
  const handleStepTwoTableValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    const params = { val: Number(val), index, row, tableHeadingOne };
    void handleStepTwoTableValueChanges(e, params, state, dispatch, myDispatch);
    dispatch({
      type: "setActiveKey",
      payload: [0, one],
    });
  };
  return row[tableHeadingOne as keyof typeof row] === TABLE_ROW_TOTAL_VOLUME ? (
    <>{value}</>
  ) : (
    <TextInput
      value={value}
      type="text"
      onChange={(e) => handleStepTwoTableValueChange(e)}
      onPaste={() => {
        handleDataPasteFromExternalFile();
      }}
    />
  );
};

export const tableCols = (
  state: ICalculatorFormState,
  dispatch: Dispatch<Action>,
  myState: InputTableFormState,
  tableHeadingOne: string,
  myDispatch: Dispatch<Action>
) => {
  const colObj: IStepTwoTableColumnObject[] = [];
  if (myState.cols.length > 0) {
    myState.cols.forEach((val: string | number) => {
      if (colObj.length === 0) {
        colObj.push({
          dataIndex: val,
          key: val,
          width: 150,
          title: val,
          className: "leftAlign",
          render: (value: string): ReactElement => getFirstColumnData(state.commodity, value),
        });
      } else {
        colObj.push({
          dataIndex: val,
          key: val,
          title: val,
          width: 150,
          className: "leftAlign",
          render: (value: string | number, row: IStepTwoTableData, index: number): ReactElement =>
            getColumnData({ row, tableHeadingOne, value, val, index }, state, dispatch, myDispatch),
        });
      }
    });
  }
  return colObj;
};
